import { filter, firstValueFrom, take } from 'rxjs';

import { Location } from '@angular/common';
import { inject } from '@angular/core';
import {
  CanDeactivateFn,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';

import { AuthService, DialogService } from '../ui/services';

export interface HasPendingChanges {
  hasPendingChanges: () => boolean;
}

export const pendingChangesGuard: CanDeactivateFn<
  HasPendingChanges
> = async component => {
  const dialogService = inject(DialogService);
  const router = inject(Router);
  const location = inject(Location);

  // Logging out signs the user out before navigating, so a signed-out user
  // is leaving the app (e.g. a 401) and has nothing left to save.
  if (!inject(AuthService).currentUser()) {
    return true;
  }

  const pendingChanges = component.hasPendingChanges
    ? component.hasPendingChanges()
    : false;

  if (pendingChanges) {
    const navigation = router.currentNavigation();
    const pagePath =
      navigation?.trigger === 'imperative' ? location.path() : null;

    const dialog = dialogService.confirm(
      'You have unsaved changes on this page.<br /><br />Do you want ' +
        'to leave this page and discard your changes?<br /><br />'
    );

    const result = await firstValueFrom(dialog.afterClosed());

    if (!result && navigation && pagePath !== null) {
      keepAddressOnCancel(router, location, navigation.id, pagePath);
    }

    return !!result;
  } else {
    return true;
  }
};

function keepAddressOnCancel(
  router: Router,
  location: Location,
  navigationId: number,
  pagePath: string
): void {
  router.events
    .pipe(
      filter(
        (
          event: Event
        ): event is NavigationCancel | NavigationEnd | NavigationError =>
          (event instanceof NavigationCancel ||
            event instanceof NavigationEnd ||
            event instanceof NavigationError) &&
          event.id === navigationId
      ),
      take(1)
    )
    .subscribe(event => {
      if (event instanceof NavigationCancel) {
        location.replaceState(pagePath, '', location.getState());
      }
    });
}
